import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ToastProvider";
import { createBillingCheckout, createBillingPortal, fetchBillingSummary, sendBillingSupportRequest, type BillingSummary } from "@/api/billing";
import { changePassword, fetchCurrentAccount, type AccountProfile, updateAccount, uploadAccountAvatar } from "@/api/account";
import { fetchMyArtist, updateMyArtist } from "@/api/artistSettings";
import { useArtistScope } from "@/lib/artistContext";
import {
  buildSpotifySearchUrl,
  extractSpotifyArtistId,
  getStoredSpotifyArtistMap,
  resolveSpotifyArtistId,
  setStoredSpotifyArtistId,
  subscribeSpotifyArtistMap,
} from "@/lib/spotify";
import { useSessionUser } from "@/hooks/useSessionUser";

export type AccountPageProps = {
  showHeader?: boolean;
};

const pricingPlans = [
  {
    title: "Standard",
    price: "$24",
    cadence: "/mo",
    highlight: "Includes 1 seat · up to 4 total",
    seatPrice: "$14 / additional seat",
  },
  {
    title: "Pro",
    price: "$44",
    cadence: "/mo",
    highlight: "Includes 1 seat · up to 10 total",
    seatPrice: "$19 / additional seat",
  },
  {
    title: "Agency",
    price: "$99",
    cadence: "/mo",
    highlight: "Includes 5 seats · up to 20 total",
    seatPrice: "$19 / additional seat",
  },
  {
    title: "Enterprise",
    price: "Custom",
    cadence: "",
    highlight: "Contact us for enterprise pricing",
    seatPrice: "Custom seats + support",
  },
];

export default function AccountPage({ showHeader = true }: AccountPageProps) {
  const [account, setAccount] = useState<AccountProfile | null>(null);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [loadingAccount, setLoadingAccount] = useState(true);
  const { artists, selectedArtistId, selectArtist, isLoading: artistsLoading, error: artistsError, isMultiArtist } =
    useArtistScope();
  const { refreshSession, user: sessionUser } = useSessionUser();
  // Private beta: Spotify/Music Analytics, Songkick and CRM contact sharing are operator-only surfaces.
  const isPlatformOwner = Boolean(sessionUser?.is_owner);
  const [spotifyArtistSelection, setSpotifyArtistSelection] = useState<string>("");
  const [spotifyIdInput, setSpotifyIdInput] = useState("");
  const [spotifyLinkStatus, setSpotifyLinkStatus] = useState<string | null>(null);
  const [spotifyLinkError, setSpotifyLinkError] = useState<string | null>(null);
  const [spotifyArtistMap, setSpotifyArtistMap] = useState<Record<string, string>>(() => getStoredSpotifyArtistMap());
  const [refreshing, setRefreshing] = useState(false);
  const toast = useToast();
  const [profileForm, setProfileForm] = useState({
    email: "",
    first_name: "",
    last_name: "",
    phone_number: "",
  });
  const [organizationForm, setOrganizationForm] = useState({
    organization_name: "",
    phone_number: "",
    address_line1: "",
    address_line2: "",
    city: "",
    region: "",
    postal_code: "",
    country: "",
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingOrganization, setSavingOrganization] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [organizationError, setOrganizationError] = useState<string | null>(null);
  const [profileStatus, setProfileStatus] = useState<string | null>(null);
  const [organizationStatus, setOrganizationStatus] = useState<string | null>(null);
  const [orgPreferencesStatus, setOrgPreferencesStatus] = useState<string | null>(null);
  const [orgPreferencesError, setOrgPreferencesError] = useState<string | null>(null);
  const [savingOrgPreferences, setSavingOrgPreferences] = useState(false);
  const [multiArtistEnabled, setMultiArtistEnabled] = useState(false);
  const [crmSyncEnabled, setCrmSyncEnabled] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [avatarStatus, setAvatarStatus] = useState<string | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [billingSummary, setBillingSummary] = useState<BillingSummary | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [billingActionLoading, setBillingActionLoading] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportType, setSupportType] = useState<"help" | "pause" | "cancel">("help");
  const [supportSubject, setSupportSubject] = useState("");
  const [supportMessage, setSupportMessage] = useState("");
  const [supportStatus, setSupportStatus] = useState<string | null>(null);
  const [supportError, setSupportError] = useState<string | null>(null);
  const [profileLinksForm, setProfileLinksForm] = useState({
    bandsintown_url: "",
    songkick_url: "",
  });
  const [profileLinksStatus, setProfileLinksStatus] = useState<string | null>(null);
  const [profileLinksError, setProfileLinksError] = useState<string | null>(null);
  const [savingProfileLinks, setSavingProfileLinks] = useState(false);
  const [securityForm, setSecurityForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [securityError, setSecurityError] = useState<string | null>(null);
  const [securityStatus, setSecurityStatus] = useState<string | null>(null);
  const [savingSecurity, setSavingSecurity] = useState(false);
  const [twoFaEnabled, setTwoFaEnabled] = useState(false);
  const [twoFaLoading, setTwoFaLoading] = useState(false);
  const [twoFaStatus, setTwoFaStatus] = useState<string | null>(null);
  const maxAvatarBytes = 2 * 1024 * 1024;

  const loadAccount = useCallback(async () => {
    setLoadingAccount(true);
    setAccountError(null);
    try {
      const profile = await fetchCurrentAccount();
      setAccount(profile);
      setTwoFaEnabled(profile.user?.two_fa_enabled ?? false);
    } catch (err) {
      setAccount(null);
      setAccountError(err instanceof Error ? err.message : "Failed to load account");
    } finally {
      setLoadingAccount(false);
    }
  }, []);

  useEffect(() => {
    void loadAccount();
  }, [loadAccount]);

  useEffect(() => {
    const normalizedRole = (account?.user?.role ?? "").toLowerCase();
    const isAdmin = ["owner", "admin"].includes(normalizedRole);
    if (!isAdmin) return;
    let cancelled = false;
    const loadBilling = async () => {
      setBillingLoading(true);
      setBillingError(null);
      try {
        const summary = await fetchBillingSummary();
        if (!cancelled) {
          setBillingSummary(summary ?? null);
        }
      } catch (err) {
        if (!cancelled) {
          setBillingError(err instanceof Error ? err.message : "Unable to load billing summary.");
        }
      } finally {
        if (!cancelled) {
          setBillingLoading(false);
        }
      }
    };
    void loadBilling();
    return () => {
      cancelled = true;
    };
  }, [account?.user?.role]);

  useEffect(() => {
    if (!account) return;
    const nameSegments = (account.user.name ?? "").trim().split(" ").filter(Boolean);
    const fallbackFirst = nameSegments[0] ?? "";
    const fallbackLast = nameSegments.slice(1).join(" ").trim();
    setProfileForm({
      email: account.user.email ?? "",
      first_name: account.user.first_name ?? fallbackFirst,
      last_name: account.user.last_name ?? fallbackLast,
      phone_number: account.user.phone_number ?? "",
    });
    const org = account.organization;
    setOrganizationForm({
      organization_name: org?.name ?? "",
      phone_number: org?.phone_number ?? "",
      address_line1: org?.address_line1 ?? "",
      address_line2: org?.address_line2 ?? "",
      city: org?.city ?? "",
      region: org?.region ?? "",
      postal_code: org?.postal_code ?? "",
      country: org?.country ?? "",
    });
    setMultiArtistEnabled((org?.account_type ?? "") === "multi_artist");
    setCrmSyncEnabled(Boolean(org?.crm_sync_enabled));
  }, [account]);

  const handleSaveOrgPreferences = useCallback(async () => {
    if (!account?.organization?.id) return;
    setSavingOrgPreferences(true);
    setOrgPreferencesError(null);
    setOrgPreferencesStatus(null);
    try {
      const payload = await updateAccount({
        account_type: multiArtistEnabled ? "multi_artist" : "single_artist",
        crm_sync_enabled: crmSyncEnabled,
      });
      setAccount(payload);
      setOrgPreferencesStatus("Preferences saved.");
      await refreshSession();
    } catch (err) {
      setOrgPreferencesError(err instanceof Error ? err.message : "Unable to update organization preferences.");
    } finally {
      setSavingOrgPreferences(false);
    }
  }, [account?.organization?.id, crmSyncEnabled, multiArtistEnabled, refreshSession]);

  useEffect(() => {
    let cancelled = false;
    const loadArtistLinks = async () => {
      try {
        const artist = await fetchMyArtist();
        if (cancelled) return;
        setProfileLinksForm((prev) => ({
          ...prev,
          bandsintown_url: artist.bandsintown_url ?? "",
          songkick_url: artist.songkick_url ?? "",
        }));
      } catch (err) {
        if (cancelled) return;
        setProfileLinksError(err instanceof Error ? err.message : "Unable to load artist profile links.");
      }
    };
    void loadArtistLinks();
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshAll = useCallback(async () => {
    setRefreshing(true);
    setAccountError(null);
    setProfileError(null);
    setOrganizationError(null);
    setProfileStatus(null);
    setOrganizationStatus(null);
    setAvatarError(null);
    setAvatarStatus(null);
    setSpotifyLinkStatus(null);
    setSpotifyLinkError(null);
    try {
      await loadAccount();
      if (refreshSession) {
        await refreshSession();
      }
    } finally {
      setRefreshing(false);
    }
  }, [loadAccount, refreshSession]);

  const handleSaveProfile = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedEmail = profileForm.email.trim();
    if (!trimmedEmail) {
      setProfileError("Email is required.");
      return;
    }
    setSavingProfile(true);
    setProfileError(null);
    setProfileStatus(null);
    try {
      await updateAccount({
        email: trimmedEmail,
        first_name: profileForm.first_name.trim() || null,
        last_name: profileForm.last_name.trim() || null,
        phone_number: profileForm.phone_number.trim() || null,
      });
      setProfileStatus("Profile saved.");
      toast.push({ title: "Account", message: "Profile information updated." });
      await loadAccount();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to save profile.";
      setProfileError(message);
      toast.push({ title: "Account", message });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveOrganization = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSavingOrganization(true);
    setOrganizationError(null);
    setOrganizationStatus(null);
    try {
      await updateAccount({
        organization_name: organizationForm.organization_name.trim() || null,
        organization_phone_number: organizationForm.phone_number.trim() || null,
        organization_address: {
          line1: organizationForm.address_line1.trim() || null,
          line2: organizationForm.address_line2.trim() || null,
          city: organizationForm.city.trim() || null,
          region: organizationForm.region.trim() || null,
          postal_code: organizationForm.postal_code.trim() || null,
          country: organizationForm.country.trim() || null,
        },
      });
      setOrganizationStatus("Organization details saved.");
      toast.push({ title: "Account", message: "Organization details updated." });
      await loadAccount();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to save organization details.";
      setOrganizationError(message);
      toast.push({ title: "Account", message });
    } finally {
      setSavingOrganization(false);
    }
  };

  useEffect(() => {
    const unsubscribe = subscribeSpotifyArtistMap((map) => setSpotifyArtistMap(map));
    return () => unsubscribe?.();
  }, []);

  useEffect(() => {
    if (!artists.length) {
      setSpotifyArtistSelection("");
      return;
    }
    setSpotifyArtistSelection((current) => {
      if (current && artists.some((artist) => artist.id === current)) {
        return current;
      }
      return artists[0].id;
    });
  }, [artists]);

  const storedSpotifyId = useMemo(() => {
    if (!spotifyArtistSelection) return null;
    const normalized = spotifyArtistSelection.trim().toLowerCase();
    return spotifyArtistMap[normalized] ?? null;
  }, [spotifyArtistSelection, spotifyArtistMap]);

  useEffect(() => {
    if (!spotifyArtistSelection) {
      setSpotifyIdInput("");
      return;
    }
    setSpotifyIdInput(storedSpotifyId ?? "");
  }, [spotifyArtistSelection, storedSpotifyId]);

  const selectedArtistRecord = useMemo(
    () => artists.find((artist) => artist.id === spotifyArtistSelection) ?? null,
    [artists, spotifyArtistSelection],
  );
  const resolvedSpotifyId = useMemo(
    () => resolveSpotifyArtistId(spotifyArtistSelection, spotifyArtistMap),
    [spotifyArtistSelection, spotifyArtistMap],
  );
  const spotifySearchUrl = useMemo(
    () => buildSpotifySearchUrl(selectedArtistRecord?.name ?? selectedArtistRecord?.id ?? ""),
    [selectedArtistRecord],
  );
  const selectedArtistLabel =
    (selectedArtistRecord?.name ?? selectedArtistRecord?.id ?? spotifyArtistSelection) || "selected artist";
  const hasCustomSpotifyMapping = Boolean(storedSpotifyId);

  const handleSaveSpotifyLink = (event: React.FormEvent) => {
    event.preventDefault();
    if (!spotifyArtistSelection) {
      setSpotifyLinkError("Select an artist first.");
      return;
    }
    setSpotifyLinkStatus(null);
    setSpotifyLinkError(null);
    const normalizedInput = extractSpotifyArtistId(spotifyIdInput || "");
    if (!normalizedInput) {
      setStoredSpotifyArtistId(spotifyArtistSelection, null);
      setSpotifyLinkStatus("Spotify link removed.");
      setSpotifyIdInput("");
      return;
    }
    if (!/^[A-Za-z0-9]{10,40}$/.test(normalizedInput)) {
      setSpotifyLinkError("That does not look like a Spotify artist ID.");
      return;
    }
    setStoredSpotifyArtistId(spotifyArtistSelection, normalizedInput);
    setSpotifyLinkStatus("Spotify artist linked.");
  };

  const handleClearSpotifyLink = () => {
    if (!spotifyArtistSelection) return;
    setStoredSpotifyArtistId(spotifyArtistSelection, null);
    setSpotifyLinkStatus("Spotify link removed.");
    setSpotifyLinkError(null);
    setSpotifyIdInput("");
  };
  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    if (!file) {
      setAvatarFile(null);
      setAvatarPreview(null);
      setAvatarError(null);
      return;
    }
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      setAvatarError("Use a PNG, JPG, or WEBP file.");
      setAvatarFile(null);
      setAvatarPreview(null);
      return;
    }
    if (file.size > maxAvatarBytes) {
      setAvatarError("Profile photo must be 2MB or smaller.");
      setAvatarFile(null);
      setAvatarPreview(null);
      return;
    }
    setAvatarError(null);
    setAvatarStatus(null);
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleAvatarUpload = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!avatarFile) {
      setAvatarError("Choose a profile photo first.");
      return;
    }
    setAvatarUploading(true);
    setAvatarError(null);
    setAvatarStatus(null);
    try {
      await uploadAccountAvatar(avatarFile);
      setAvatarFile(null);
      setAvatarPreview(null);
      setAvatarStatus("Profile photo updated.");
      if (refreshSession) {
        await refreshSession();
      }
      await loadAccount();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to upload profile photo.";
      setAvatarError(message);
      toast.push({ title: "Account", message });
    } finally {
      setAvatarUploading(false);
    }
  };

  useEffect(() => {
    if (!avatarPreview) return;
    return () => {
      URL.revokeObjectURL(avatarPreview);
    };
  }, [avatarPreview]);

  const passwordRules = useMemo(() => {
    const password = securityForm.new_password;
    return {
      length: password.length >= 12,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      symbol: /[^A-Za-z0-9]/.test(password),
      noSpaces: !/\s/.test(password),
    };
  }, [securityForm.new_password]);

  const isPasswordStrong = useMemo(
    () => Object.values(passwordRules).every(Boolean),
    [passwordRules],
  );

  const handleSecurityChange = (field: keyof typeof securityForm, value: string) => {
    setSecurityForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleTwoFaToggle = async (enabled: boolean) => {
    setTwoFaLoading(true);
    setTwoFaStatus(null);
    try {
      const endpoint = enabled ? "/auth/2fa/enable" : "/auth/2fa/disable";
      const token = localStorage.getItem("token");
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error("Failed to update 2FA setting");
      setTwoFaEnabled(enabled);
      setTwoFaStatus(enabled ? "Two-factor authentication enabled." : "Two-factor authentication disabled.");
    } catch {
      setTwoFaStatus("Failed to update. Please try again.");
    } finally {
      setTwoFaLoading(false);
    }
  };

  const handleProfileLinksChange = (field: keyof typeof profileLinksForm, value: string) => {
    setProfileLinksForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleProfileLinksSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setProfileLinksError(null);
    setProfileLinksStatus(null);
    setSavingProfileLinks(true);
    try {
      await updateMyArtist({
        bandsintown_url: profileLinksForm.bandsintown_url.trim() || undefined,
        songkick_url: profileLinksForm.songkick_url.trim() || undefined,
      });
      setProfileLinksStatus("Profile links updated.");
      toast.push({ title: "Artists", message: "Bandsintown link saved." });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to update artist links.";
      setProfileLinksError(message);
      toast.push({ title: "Artists", message });
    } finally {
      setSavingProfileLinks(false);
    }
  };

  const handlePasswordUpdate = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSecurityError(null);
    setSecurityStatus(null);

    if (!securityForm.current_password || !securityForm.new_password || !securityForm.confirm_password) {
      setSecurityError("Fill out your current password and the new password twice.");
      return;
    }
    if (securityForm.new_password !== securityForm.confirm_password) {
      setSecurityError("New passwords do not match.");
      return;
    }
    if (securityForm.current_password === securityForm.new_password) {
      setSecurityError("New password must be different from the current password.");
      return;
    }
    if (!isPasswordStrong) {
      setSecurityError("New password does not meet the security requirements.");
      return;
    }

    setSavingSecurity(true);
    try {
      await changePassword({
        current_password: securityForm.current_password,
        new_password: securityForm.new_password,
        confirm_password: securityForm.confirm_password,
      });
      setSecurityStatus("Password updated.");
      setSecurityForm({ current_password: "", new_password: "", confirm_password: "" });
      toast.push({ title: "Security", message: "Password updated successfully." });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to update password.";
      setSecurityError(message);
      toast.push({ title: "Security", message });
    } finally {
      setSavingSecurity(false);
    }
  };

  const formatMoney = (amount?: number | null, currency?: string | null) => {
    if (typeof amount !== "number" || !currency) return null;
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency.toUpperCase(),
        maximumFractionDigits: 0,
      }).format(amount / 100);
    } catch {
      return `$${(amount / 100).toFixed(0)}`;
    }
  };

  const formatDate = (value?: string | null) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const statusLabel = (status?: string | null) => {
    if (!status) return "Not subscribed";
    return status.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const handleStartCheckout = async () => {
    setBillingActionLoading(true);
    try {
      const response = await createBillingCheckout({});
      window.location.assign(response.checkout_url);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to start checkout.";
      toast.push({ title: "Billing", message });
    } finally {
      setBillingActionLoading(false);
    }
  };

  const handleOpenPortal = async () => {
    setBillingActionLoading(true);
    try {
      const response = await createBillingPortal();
      window.location.assign(response.portal_url);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to open billing portal.";
      toast.push({ title: "Billing", message });
    } finally {
      setBillingActionLoading(false);
    }
  };

  const openSupportDialog = (type: "help" | "pause" | "cancel") => {
    setSupportType(type);
    setSupportSubject("");
    setSupportMessage("");
    setSupportStatus(null);
    setSupportError(null);
    setSupportOpen(true);
  };

  const handleSupportSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSupportError(null);
    setSupportStatus(null);
    if (!supportMessage.trim()) {
      setSupportError("Tell us what you need help with.");
      return;
    }
    setBillingActionLoading(true);
    try {
      await sendBillingSupportRequest({
        request_type: supportType,
        subject: supportSubject.trim() || undefined,
        message: supportMessage.trim(),
      });
      setSupportStatus("Request sent. We'll follow up by email.");
      toast.push({ title: "Billing", message: "Support request sent." });
      setSupportOpen(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to send request.";
      setSupportError(message);
      toast.push({ title: "Billing", message });
    } finally {
      setBillingActionLoading(false);
    }
  };

  const isAdminRole = ["owner", "admin"].includes((account?.user?.role ?? "").toLowerCase());

  return (
    <div className="aa-page-shell space-y-8">
      {showHeader && (
        <header className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-primary/80">Account</p>
          <h1 className="aa-page-title">Account & Settings</h1>
          <p className="text-sm text-muted-foreground">Review your profile and organization details.</p>
        </header>
      )}

      <section className="grid gap-4 lg:grid-cols-3">
        <div className="aa-glass-card p-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Profile</h2>
              <p className="text-xs text-muted-foreground">Signed-in identity</p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="px-2 text-xs"
              onClick={refreshAll}
              disabled={refreshing}
            >
              {refreshing ? "Refreshing…" : "Refresh"}
            </Button>
          </div>
          {loadingAccount ? (
            <p className="mt-3 text-sm text-muted-foreground">Loading profile…</p>
          ) : accountError ? (
            <p className="mt-3 text-sm text-rose-400">{accountError}</p>
          ) : account ? (
            <>
              <form className="mt-4 space-y-4" onSubmit={handleSaveProfile}>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label>First name</Label>
                    <Input
                      value={profileForm.first_name}
                      onChange={(event) =>
                        setProfileForm((prev) => ({ ...prev, first_name: event.target.value }))
                      }
                      disabled={savingProfile || loadingAccount}
                    />
                  </div>
                  <div>
                    <Label>Last name</Label>
                    <Input
                      value={profileForm.last_name}
                      onChange={(event) =>
                        setProfileForm((prev) => ({ ...prev, last_name: event.target.value }))
                      }
                      disabled={savingProfile || loadingAccount}
                    />
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={profileForm.email}
                      onChange={(event) =>
                        setProfileForm((prev) => ({ ...prev, email: event.target.value }))
                      }
                      required
                      disabled={savingProfile || loadingAccount}
                    />
                  </div>
                  <div>
                    <Label>Phone number</Label>
                    <Input
                      type="tel"
                      value={profileForm.phone_number}
                      onChange={(event) =>
                        setProfileForm((prev) => ({ ...prev, phone_number: event.target.value }))
                      }
                      disabled={savingProfile || loadingAccount}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Role: {account.user.role}</p>
                {profileError && <p className="text-xs text-rose-400">{profileError}</p>}
                {profileStatus && <p className="text-xs text-emerald-300">{profileStatus}</p>}
                <div className="flex justify-end">
                  <Button type="submit" disabled={savingProfile || loadingAccount}>
                    {savingProfile ? "Saving…" : "Save profile"}
                  </Button>
                </div>
              </form>
              <form className="mt-4 space-y-3" onSubmit={handleAvatarUpload}>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                  <div className="h-20 w-20 overflow-hidden rounded-full border border-border/70 bg-muted/40">
                    <img
                      src={avatarPreview ?? account.user.avatar_url ?? "/aafavicon.png"}
                      alt={account.user.name ?? account.user.email ?? "Profile"}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="flex-1 space-y-2">
                    <Label>Profile photo</Label>
                    <Input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={handleAvatarChange}
                      disabled={avatarUploading}
                    />
                    <p className="text-xs text-muted-foreground">PNG, JPG, or WEBP • 2MB max.</p>
                    {avatarError && <p className="text-xs text-rose-400">{avatarError}</p>}
                    {avatarStatus && <p className="text-xs text-emerald-300">{avatarStatus}</p>}
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button type="submit" disabled={!avatarFile || avatarUploading}>
                    {avatarUploading ? "Uploading…" : "Upload photo"}
                  </Button>
                </div>
              </form>
            </>
          ) : null}
        </div>

        <div className="aa-glass-card p-5 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Artist</h2>
            <p className="text-xs text-muted-foreground">Manage artist profile, assets, and links.</p>
          </div>
          <div className="space-y-3 text-sm text-foreground/80">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p>
                {artistsLoading
                  ? "Loading artists…"
                  : artists.length
                    ? `Active: ${artists.find((artist) => artist.id === selectedArtistId)?.name || selectedArtistId}`
                    : "No artists linked yet."}
              </p>
              {artists.length > 1 && (
                <span className="rounded-full border border-border/60 bg-background/40 px-2 py-0.5 text-[11px] text-muted-foreground">
                  {artists.length} total
                </span>
              )}
            </div>
            {isMultiArtist && artists.length > 1 && (
              <div>
                <Label htmlFor="account-artist-selector" className="text-xs uppercase tracking-wide text-muted-foreground">
                  Switch artist
                </Label>
                <select
                  id="account-artist-selector"
                  className="mt-1 w-full rounded border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                  value={selectedArtistId ?? ""}
                  onChange={(event) => selectArtist(event.target.value)}
                  disabled={artistsLoading}
                >
                  {artists.map((artistOption) => (
                    <option key={artistOption.id} value={artistOption.id}>
                      {artistOption.name || artistOption.id}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Update band photos, logos, social channels, and media links for the active artist.
            </p>
          </div>
          <div className="flex justify-end">
            <Button asChild>
              <Link to="/settings/artist">Open artist settings</Link>
            </Button>
          </div>
        </div>

        {false && (
        <div className="aa-glass-card p-5 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Email</h2>
            <p className="text-xs text-muted-foreground">Connect IMAP and update mailbox credentials.</p>
          </div>
          <p className="text-xs text-muted-foreground">
            Manage your inbox connection and verify SMTP/IMAP access for sending and sync.
          </p>
          <div className="flex justify-end">
            <Button asChild>
              <Link to="/settings/email">Open email settings</Link>
            </Button>
          </div>
        </div>
        )}

        <div className="aa-glass-card p-5">
          <h2 className="text-lg font-semibold text-foreground">Organization</h2>
          {loadingAccount ? (
            <p className="mt-3 text-sm text-muted-foreground">Loading organization…</p>
          ) : accountError ? (
            <p className="mt-3 text-sm text-rose-400">{accountError}</p>
          ) : account ? (
            <form className="mt-4 space-y-4" onSubmit={handleSaveOrganization}>
              <div>
                <Label>Organization name</Label>
                <Input
                  value={organizationForm.organization_name}
                  onChange={(event) =>
                    setOrganizationForm((prev) => ({ ...prev, organization_name: event.target.value }))
                  }
                  disabled={savingOrganization || loadingAccount}
                />
              </div>
              <div>
                <Label>Phone number</Label>
                <Input
                  type="tel"
                  value={organizationForm.phone_number}
                  onChange={(event) =>
                    setOrganizationForm((prev) => ({ ...prev, phone_number: event.target.value }))
                  }
                  disabled={savingOrganization || loadingAccount}
                />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Street address</Label>
                  <Input
                    value={organizationForm.address_line1}
                    onChange={(event) =>
                      setOrganizationForm((prev) => ({ ...prev, address_line1: event.target.value }))
                    }
                    disabled={savingOrganization || loadingAccount}
                  />
                </div>
                <div>
                  <Label>Address line 2</Label>
                  <Input
                    value={organizationForm.address_line2}
                    onChange={(event) =>
                      setOrganizationForm((prev) => ({ ...prev, address_line2: event.target.value }))
                    }
                    disabled={savingOrganization || loadingAccount}
                  />
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-3">
                <div>
                  <Label>City</Label>
                  <Input
                    value={organizationForm.city}
                    onChange={(event) =>
                      setOrganizationForm((prev) => ({ ...prev, city: event.target.value }))
                    }
                    disabled={savingOrganization || loadingAccount}
                  />
                </div>
                <div>
                  <Label>Region/State</Label>
                  <Input
                    value={organizationForm.region}
                    onChange={(event) =>
                      setOrganizationForm((prev) => ({ ...prev, region: event.target.value }))
                    }
                    disabled={savingOrganization || loadingAccount}
                  />
                </div>
                <div>
                  <Label>Postal code</Label>
                  <Input
                    value={organizationForm.postal_code}
                    onChange={(event) =>
                      setOrganizationForm((prev) => ({ ...prev, postal_code: event.target.value }))
                    }
                    disabled={savingOrganization || loadingAccount}
                  />
                </div>
              </div>
              <div>
                <Label>Country</Label>
                <Input
                  value={organizationForm.country}
                  onChange={(event) =>
                    setOrganizationForm((prev) => ({ ...prev, country: event.target.value }))
                  }
                  disabled={savingOrganization || loadingAccount}
                />
              </div>
              <div className="pt-4 border-t border-border/60">
                <p className="text-sm font-semibold text-foreground">Preferences</p>
                <div className="mt-3 space-y-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-foreground">Enable multi-artist workspace</p>
                      <p className="text-xs text-muted-foreground">
                        Available to Agency plans. Allows up to 10 artists in this organization. Contact
                        admin@artistascend.app for Enterprise pricing.
                      </p>
                    </div>
                    <Switch
                      checked={multiArtistEnabled}
                      onCheckedChange={setMultiArtistEnabled}
                      disabled={!account?.organization?.multi_artist_available}
                      aria-label="Enable multi-artist workspace"
                    />
                  </div>
                  {!account?.organization?.multi_artist_available && (
                    <p className="text-xs text-muted-foreground">
                      Upgrade to an Agency plan to unlock multi-artist access. Contact
                      admin@artistascend.app for Enterprise pricing.
                    </p>
                  )}
                  {isPlatformOwner && (
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-foreground">Sync contacts across artists</p>
                      <p className="text-xs text-muted-foreground">
                        Automatically share CRM contacts between artists in this organization.
                      </p>
                    </div>
                    <Switch
                      checked={crmSyncEnabled}
                      onCheckedChange={setCrmSyncEnabled}
                      aria-label="Sync contacts across artists"
                    />
                  </div>
                  )}
                  {orgPreferencesError && <p className="text-xs text-rose-400">{orgPreferencesError}</p>}
                  {orgPreferencesStatus && <p className="text-xs text-emerald-300">{orgPreferencesStatus}</p>}
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      onClick={handleSaveOrgPreferences}
                      disabled={savingOrgPreferences || loadingAccount}
                    >
                      {savingOrgPreferences ? "Saving…" : "Save preferences"}
                    </Button>
                  </div>
                </div>
              </div>
              {organizationError && <p className="text-xs text-rose-400">{organizationError}</p>}
              {organizationStatus && <p className="text-xs text-emerald-300">{organizationStatus}</p>}
              <div className="flex justify-end">
                <Button type="submit" disabled={savingOrganization || loadingAccount}>
                  {savingOrganization ? "Saving…" : "Save organization"}
                </Button>
              </div>
            </form>
          ) : null}
          {account?.teams?.length ? (
            <div className="mt-4">
              <p className="text-xs uppercase text-muted-foreground">Teams</p>
              <ul className="mt-1 text-sm text-foreground/80">
                {account.teams.map((team) => (
                  <li key={team.id}>{team.name || team.id}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        <div className="aa-glass-card p-5 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Billing</h2>
              <p className="text-xs text-muted-foreground">Visible to admins and owners.</p>
            </div>
            {billingSummary?.billing_exempt ? (
              <span className="rounded-full border border-emerald-400/40 bg-emerald-500/10 px-2 py-1 text-[11px] font-semibold text-emerald-200">
                Billing exempt
              </span>
            ) : null}
          </div>
          {!isAdminRole ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Billing is managed by an admin or owner.</p>
              <Button variant="secondary" onClick={() => openSupportDialog("help")}>
                Contact help
              </Button>
            </div>
          ) : billingLoading ? (
            <p className="text-sm text-muted-foreground">Loading billing…</p>
          ) : billingError ? (
            <p className="text-sm text-rose-400">{billingError}</p>
          ) : billingSummary ? (
            <div className="space-y-4 text-sm text-foreground/80">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Plan</p>
                  <p className="text-base font-semibold text-foreground">
                    {billingSummary.plan_name ?? billingSummary.tier ?? "Standard"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase text-muted-foreground">Price</p>
                  <p className="text-base font-semibold text-foreground">
                    {formatMoney(billingSummary.price_amount, billingSummary.price_currency) ??
                      (billingSummary.subscription_status ? "Managed in Stripe" : "Not subscribed")}
                    {billingSummary.price_interval ? (
                      <span className="text-xs text-muted-foreground"> / {billingSummary.price_interval}</span>
                    ) : null}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Status</p>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-1 text-[11px] font-semibold ${
                      ["active", "trialing"].includes(billingSummary.subscription_status ?? "")
                        ? "bg-emerald-500/10 text-emerald-200"
                        : ["past_due", "incomplete", "unpaid"].includes(billingSummary.subscription_status ?? "")
                          ? "bg-amber-500/10 text-amber-200"
                          : billingSummary.subscription_status
                            ? "bg-rose-500/10 text-rose-200"
                            : "bg-muted/60 text-muted-foreground"
                    }`}
                  >
                    {statusLabel(billingSummary.subscription_status)}
                  </span>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase text-muted-foreground">Next renewal</p>
                  <p className="text-sm text-foreground">
                    {formatDate(billingSummary.next_invoice_date ?? billingSummary.current_period_end) ?? "—"}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-xs uppercase text-muted-foreground">Seats</p>
                <div className="mt-2 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>
                      {billingSummary.seat_count} active
                      {typeof billingSummary.seat_limit === "number" ? ` / ${billingSummary.seat_limit} limit` : ""}
                    </span>
                    {typeof billingSummary.seat_limit === "number" ? (
                      <span className="text-xs text-muted-foreground">
                        {Math.min(
                          100,
                          Math.round((billingSummary.seat_count / Math.max(1, billingSummary.seat_limit)) * 100),
                        )}
                        %
                      </span>
                    ) : null}
                  </div>
                  {typeof billingSummary.seat_limit === "number" ? (
                    <div className="h-2 w-full rounded-full bg-muted/60">
                      <div
                        className="h-2 rounded-full bg-emerald-400/80"
                        style={{
                          width: `${Math.min(
                            100,
                            Math.round((billingSummary.seat_count / Math.max(1, billingSummary.seat_limit)) * 100),
                          )}%`,
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              </div>

              <div>
                <p className="text-xs uppercase text-muted-foreground">Payment method</p>
                <p className="text-sm text-foreground">
                  {billingSummary.payment_method_brand && billingSummary.payment_method_last4
                    ? `${billingSummary.payment_method_brand.toUpperCase()} •••• ${billingSummary.payment_method_last4}`
                    : "Add a payment method in Stripe"}
                </p>
              </div>

              {/* Plans, checkout and payment changes live on the canonical billing page. */}
              <div className="flex flex-wrap gap-2">
                <Button asChild>
                  <Link to="/settings/billing">Manage billing</Link>
                </Button>
                <Button variant="secondary" onClick={() => openSupportDialog("help")}>
                  Contact help
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">No billing data available.</p>
              <Button variant="secondary" onClick={() => openSupportDialog("help")}>
                Contact help
              </Button>
            </div>
          )}
        </div>

        <div className="aa-glass-card p-5 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Security</h2>
            <p className="text-sm text-muted-foreground">Update your password to keep your account secure.</p>
          </div>
          <form className="space-y-4" onSubmit={handlePasswordUpdate}>
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <Label>Current password</Label>
                <Input
                  type="password"
                  value={securityForm.current_password}
                  onChange={(event) => handleSecurityChange("current_password", event.target.value)}
                  autoComplete="current-password"
                  disabled={savingSecurity}
                />
              </div>
              <div>
                <Label>New password</Label>
                <Input
                  type="password"
                  value={securityForm.new_password}
                  onChange={(event) => handleSecurityChange("new_password", event.target.value)}
                  autoComplete="new-password"
                  disabled={savingSecurity}
                />
              </div>
              <div>
                <Label>Confirm new password</Label>
                <Input
                  type="password"
                  value={securityForm.confirm_password}
                  onChange={(event) => handleSecurityChange("confirm_password", event.target.value)}
                  autoComplete="new-password"
                  disabled={savingSecurity}
                />
              </div>
            </div>
            <div className="rounded border border-border bg-background/40 p-3 text-xs text-muted-foreground">
              <p className="font-semibold text-foreground">Password requirements</p>
              <ul className="mt-2 space-y-1">
                <li className={passwordRules.length ? "text-emerald-300" : undefined}>At least 12 characters</li>
                <li className={passwordRules.uppercase ? "text-emerald-300" : undefined}>1 uppercase letter</li>
                <li className={passwordRules.lowercase ? "text-emerald-300" : undefined}>1 lowercase letter</li>
                <li className={passwordRules.number ? "text-emerald-300" : undefined}>1 number</li>
                <li className={passwordRules.symbol ? "text-emerald-300" : undefined}>1 symbol</li>
                <li className={passwordRules.noSpaces ? "text-emerald-300" : undefined}>No spaces</li>
              </ul>
            </div>
            {securityError && <p className="text-xs text-rose-400">{securityError}</p>}
            {securityStatus && <p className="text-xs text-emerald-300">{securityStatus}</p>}
            <div className="flex justify-end">
              <Button type="submit" disabled={savingSecurity}>
                {savingSecurity ? "Updating…" : "Update password"}
              </Button>
            </div>
          </form>

          <div className="border-t border-border pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">Two-factor authentication</p>
                <p className="text-xs text-muted-foreground">
                  Require an email verification code each time you sign in.
                </p>
              </div>
              <Switch
                checked={twoFaEnabled}
                onCheckedChange={handleTwoFaToggle}
                disabled={twoFaLoading}
              />
            </div>
            {twoFaStatus && (
              <p className={`mt-2 text-xs ${twoFaStatus.startsWith("Failed") ? "text-rose-400" : "text-emerald-300"}`}>
                {twoFaStatus}
              </p>
            )}
          </div>
        </div>

        {isPlatformOwner && (
        <div className="space-y-4">
          <section className="aa-glass-card p-5 space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Link Spotify</h2>
                <p className="text-sm text-muted-foreground">
                  Connect each tracked artist to the right Spotify profile to unlock the Music Analytics dashboard.
                </p>
              </div>
              <a
                href={spotifySearchUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs font-semibold text-primary/80 hover:text-foreground"
              >
                Find on Spotify
              </a>
            </div>
            {artistsError && <p className="text-xs text-rose-400">{artistsError}</p>}
            <form className="space-y-3" onSubmit={handleSaveSpotifyLink}>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-xs uppercase text-muted-foreground">Artist</label>
                  <select
                    className="mt-1 w-full rounded border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                    value={spotifyArtistSelection}
                    onChange={(event) => setSpotifyArtistSelection(event.target.value)}
                    disabled={artistsLoading || !artists.length}
                  >
                    {artistsLoading ? (
                      <option value="">Loading artists…</option>
                    ) : !artists.length ? (
                      <option value="">No artists</option>
                    ) : (
                      artists.map((artistOption) => (
                        <option key={artistOption.id} value={artistOption.id}>
                          {artistOption.name || artistOption.id}
                        </option>
                      ))
                    )}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase text-muted-foreground">Spotify Artist ID or URL</label>
                  <input
                    type="text"
                    className="mt-1 w-full rounded border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                    value={spotifyIdInput}
                    onChange={(event) => setSpotifyIdInput(event.target.value)}
                    placeholder="37i9dQZF1DX..."
                    disabled={!spotifyArtistSelection}
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="submit" size="sm" disabled={!spotifyArtistSelection || artistsLoading}>
                  Save Spotify Link
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={handleClearSpotifyLink} disabled={!spotifyArtistSelection}>
                  Remove Link
                </Button>
                <span className="text-xs text-muted-foreground">
                  Accepts a Spotify URL (ex. https://open.spotify.com/artist/ID) or the raw artist ID.
                </span>
              </div>
              {spotifyLinkStatus && <p className="text-xs text-emerald-300">{spotifyLinkStatus}</p>}
              {spotifyLinkError && <p className="text-xs text-rose-400">{spotifyLinkError}</p>}
            </form>
            <div className="rounded border border-border bg-background/40 p-3 text-xs text-foreground/70">
              {resolvedSpotifyId ? (
                <>
                  <p>
                    Active Spotify ID for <strong>{selectedArtistLabel}</strong>:{" "}
                    <code className="text-foreground">{resolvedSpotifyId}</code>
                  </p>
                  {!hasCustomSpotifyMapping && (
                    <p className="mt-1 text-muted-foreground">Using default or fallback mapping. Saving a custom ID overrides it.</p>
                  )}
                </>
              ) : (
                <p>No Spotify artist linked yet. Saving an ID enables the Music Analytics dashboard.</p>
              )}
            </div>
          </section>

          <section className="aa-glass-card p-5 space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Link Bandsintown + Songkick</h2>
              <p className="text-sm text-muted-foreground">
                Store profile links for the primary artist. Songkick wiring is queued for a future release.
              </p>
            </div>
            <form className="space-y-4" onSubmit={handleProfileLinksSave}>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Bandsintown profile URL</Label>
                  <Input
                    value={profileLinksForm.bandsintown_url}
                    onChange={(event) => handleProfileLinksChange("bandsintown_url", event.target.value)}
                    placeholder="https://www.bandsintown.com/a/your-artist"
                    disabled={savingProfileLinks}
                  />
                </div>
                <div>
                  <Label>Songkick profile URL</Label>
                  <Input
                    value={profileLinksForm.songkick_url}
                    onChange={(event) => handleProfileLinksChange("songkick_url", event.target.value)}
                    placeholder="https://www.songkick.com/artists/your-artist"
                    disabled
                  />
                  <p className="text-xs text-muted-foreground">TODO: Wire Songkick integration.</p>
                </div>
              </div>
              {profileLinksError && <p className="text-xs text-rose-400">{profileLinksError}</p>}
              {profileLinksStatus && <p className="text-xs text-emerald-300">{profileLinksStatus}</p>}
              <div className="flex justify-end">
                <Button type="submit" disabled={savingProfileLinks}>
                  {savingProfileLinks ? "Saving…" : "Save links"}
                </Button>
              </div>
            </form>
          </section>
        </div>
        )}
      </section>

      <Dialog open={supportOpen} onOpenChange={setSupportOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Billing support request</DialogTitle>
            <DialogDescription>
              Send a note to our team at admin@artistascend.app.
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleSupportSubmit}>
            <div>
              <Label>Request type</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {(["help", "pause", "cancel"] as const).map((option) => (
                  <Button
                    key={option}
                    type="button"
                    variant={supportType === option ? "secondary" : "outline"}
                    size="sm"
                    className="rounded-full px-3 text-xs font-semibold"
                    onClick={() => setSupportType(option)}
                  >
                    {option === "help" ? "Help" : option === "pause" ? "Pause" : "Cancel"}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <Label>Subject (optional)</Label>
              <Input
                value={supportSubject}
                onChange={(event) => setSupportSubject(event.target.value)}
                placeholder="Short summary"
              />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                rows={5}
                value={supportMessage}
                onChange={(event) => setSupportMessage(event.target.value)}
                placeholder="Tell us what you need, and include any timing details."
              />
            </div>
            {supportError && <p className="text-xs text-rose-400">{supportError}</p>}
            {supportStatus && <p className="text-xs text-emerald-300">{supportStatus}</p>}
            <DialogFooter className="flex flex-wrap justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setSupportOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={billingActionLoading}>
                {billingActionLoading ? "Sending…" : "Send request"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
}
